import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import {
  Copy,
  Check,
  Download,
  FileSpreadsheet,
  Quote,
  BookOpen,
  Code2,
  BarChart3,
  Globe,
  Users,
  Layers
} from "lucide-react";
import { API, COLORS } from "../../lib/constants";
import { trackEvent } from "../../lib/track";

const CITATION_STYLES = {
  apa: "APA",
  bibtex: "BibTeX",
  chicago: "Chicago"
};

const generateCitation = (style, stats) => {
  const today = new Date().toISOString().split("T")[0];
  const year = new Date().getFullYear();
  const n = stats?.total_apps || "N";
  const countries = stats?.num_countries || "N";

  switch (style) {
    case "apa":
      return `Jha, Priyanka. The Conscious Innovation Index (CII): A 7-Dimension Framework for Measuring the Ethical Alignment of Artificial Intelligence (2026). Available at SSRN: https://ssrn.com/abstract=6492398 or http://dx.doi.org/10.2139/ssrn.6492398\n\nJha, Priyanka. The Possibility Engine: A Data-Grounded Framework for Software-Hardware AI Convergence and Future Capability Mapping (2026). Available at SSRN: https://ssrn.com/abstract=6481799 or http://dx.doi.org/10.2139/ssrn.6481799`;
    case "bibtex":
      return `@article{jha_2026_cii,
  title   = {The Conscious Innovation Index (CII): A 7-Dimension Framework for Measuring the Ethical Alignment of Artificial Intelligence},
  author  = {Jha, Priyanka},
  year    = {2026},
  doi     = {10.2139/ssrn.6492398},
  url     = {https://ssrn.com/abstract=6492398}
}

@article{jha_2026_possibility,
  title   = {The Possibility Engine: A Data-Grounded Framework for Software-Hardware AI Convergence and Future Capability Mapping},
  author  = {Jha, Priyanka},
  year    = {2026},
  doi     = {10.2139/ssrn.6481799},
  url     = {https://ssrn.com/abstract=6481799}
}`;
    case "chicago":
      return `Jha, Priyanka. "The Conscious Innovation Index (CII): A 7-Dimension Framework for Measuring the Ethical Alignment of Artificial Intelligence." 2026. https://ssrn.com/abstract=6492398.\n\nJha, Priyanka. "The Possibility Engine: A Data-Grounded Framework for Software-Hardware AI Convergence and Future Capability Mapping." 2026. https://ssrn.com/abstract=6481799.`;
    default:
      return "";
  }
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    trackEvent("citation_copied", "share_cite");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      data-testid="copy-citation-btn"
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

export const ShareCitePanel = ({ inline = false }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStyle, setActiveStyle] = useState("apa");
  const [downloading, setDownloading] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/citation-stats`).then(res => {
      setStats(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const downloadCard = useCallback(async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0c0c1d",
        scale: 2,
        useCORS: true
      });
      const link = document.createElement("a");
      link.download = `aioo-citation-card-${new Date().toISOString().split("T")[0]}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      void 0; // error handled silently
    } finally {
      setDownloading(false);
    }
  }, []);

  const downloadCSV = useCallback(async () => {
    setCsvDownloading(true);
    try {
      const res = await axios.get(`${API}/export/csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `aioo-dataset-${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      void 0; // error handled silently
    } finally {
      setCsvDownloading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
        <span className="ml-3 text-muted-foreground text-sm">Loading citation data...</span>
      </div>
    );
  }

  if (!stats) return null;

  const orientations = stats.orientations || {};
  const founders = stats.founders || {};
  const citationText = generateCitation(activeStyle, stats);

  return (
    <div className={`space-y-6 ${inline ? "" : "animate-fade-in"}`}>
      {/* Citation Card (used for image export) */}
      <div
        ref={cardRef}
        className="rounded-xl overflow-hidden border border-border"
        style={{ background: "linear-gradient(135deg, #0c0c1d 0%, #141428 50%, #0f1a2e 100%)" }}
      >
        <div className="p-6 space-y-5">
          {/* Card Header */}
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg" style={{ background: "rgba(139,92,246,0.15)" }}>
              <BarChart3 className="w-6 h-6" style={{ color: "#8b5cf6" }} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">AI Orientation Observatory</h3>
              <p className="text-sm" style={{ color: "#94a3b8" }}>Open Research Dataset</p>
            </div>
          </div>

          {/* Live Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg p-3 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <Layers className="w-4 h-4 mx-auto mb-1" style={{ color: "#8b5cf6" }} />
              <p className="text-2xl font-bold text-white" data-testid="cite-total-apps">{stats.total_apps?.toLocaleString()}</p>
              <p className="text-xs" style={{ color: "#94a3b8" }}>AI Apps</p>
            </div>
            <div className="rounded-lg p-3 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <Globe className="w-4 h-4 mx-auto mb-1" style={{ color: "#3b82f6" }} />
              <p className="text-2xl font-bold text-white" data-testid="cite-countries">{stats.num_countries}</p>
              <p className="text-xs" style={{ color: "#94a3b8" }}>Countries</p>
            </div>
            <div className="rounded-lg p-3 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <Users className="w-4 h-4 mx-auto mb-1" style={{ color: "#ec4899" }} />
              <p className="text-2xl font-bold text-white">{(founders.female_only || 0) + (founders.mixed || 0)}</p>
              <p className="text-xs" style={{ color: "#94a3b8" }}>Diverse Teams</p>
            </div>
            <div className="rounded-lg p-3 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <BookOpen className="w-4 h-4 mx-auto mb-1" style={{ color: "#10b981" }} />
              <p className="text-2xl font-bold text-white">4</p>
              <p className="text-xs" style={{ color: "#94a3b8" }}>Orientations</p>
            </div>
          </div>

          {/* Orientation Breakdown Bar */}
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: "#94a3b8" }}>Value Orientation Distribution</p>
            <div className="flex rounded-full overflow-hidden h-3">
              {[
                { key: "utility", color: COLORS.utility },
                { key: "care", color: COLORS.care },
                { key: "creative", color: COLORS.creative },
                { key: "extraction", color: COLORS.extraction }
              ].map(o => {
                const pct = stats.total_apps ? ((orientations[o.key] || 0) / stats.total_apps) * 100 : 0;
                return pct > 0 ? (
                  <div key={o.key} style={{ width: `${pct}%`, backgroundColor: o.color }} title={`${o.key}: ${pct.toFixed(1)}%`} />
                ) : null;
              })}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
              {[
                { key: "utility", label: "Utility", color: COLORS.utility },
                { key: "care", label: "Care", color: COLORS.care },
                { key: "creative", label: "Creative", color: COLORS.creative },
                { key: "extraction", label: "Extraction", color: COLORS.extraction }
              ].map(o => (
                <span key={o.key} className="flex items-center gap-1.5 text-xs" style={{ color: "#94a3b8" }}>
                  <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: o.color }} />
                  {o.label} ({orientations[o.key] || 0})
                </span>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs pt-3" style={{ color: "#64748b", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <span style={{ color: "#94a3b8" }}>amplifyingconsciousinnovation.com</span>
            <span>&copy; {new Date().getFullYear()} AI Orientation Observatory</span>
          </div>
        </div>
      </div>

      {/* Download + Share */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-3">
          <button onClick={downloadCard} disabled={downloading} data-testid="download-card-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50">
            <Download className="w-4 h-4" />
            {downloading ? "Generating..." : "1. Download Card"}
          </button>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border border-border hover:bg-muted transition-colors">
            2. Share on LinkedIn
          </a>
        </div>
        <p className="text-[10px] text-muted-foreground/60">Download the card image first, then attach it when posting for a rich visual share</p>
      </div>

      {/* Citation Formats */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="flex border-b border-border">
          {Object.entries(CITATION_STYLES).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveStyle(key)}
              data-testid={`cite-style-${key}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${
                activeStyle === key
                  ? "bg-muted text-foreground border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
            >
              {key === "bibtex" ? <Code2 className="w-3.5 h-3.5" /> : <Quote className="w-3.5 h-3.5" />}
              {label}
            </button>
          ))}
        </div>
        <div className="p-4">
          <pre
            className="text-sm font-mono whitespace-pre-wrap leading-relaxed p-4 bg-muted/30 rounded-lg border border-border text-foreground"
            data-testid="citation-text"
          >
            {citationText}
          </pre>
          <div className="mt-3 flex justify-end">
            <CopyButton text={citationText} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareCitePanel;
